import {
  Button,
  Card,
  CardActions,
  CardContent,
  colors,
  Grid,
  Modal,
  Switch,
  Theme,
  Typography
} from '@material-ui/core';
import {makeStyles} from '@material-ui/styles';
import clsx from 'clsx';
import React, {useEffect, useState} from 'react';
import {T} from 'react-polyglot-hooks';
import {RoomParty} from '../../../graphql/groups.datatypes';
import splitRoom, {SplitRoomInput} from './splitRoom.api';
import {LoadingOverlay} from '../../../components/LoadingOverlay';
import {useSnackbar} from 'notistack';
import {checkRoomAvailable} from '../../Resources/AvailableHotelModal/AvailableHotelModal';
import moment from 'moment';

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    outline: 'none',
    boxShadow: theme.shadows[20],
    width: 700,
    maxHeight: '90%',
    overflowY: 'auto',
    maxWidth: '95%'
  },
  container: {
    marginTop: theme.spacing(3)
  },
  actions: {
    justifyContent: 'flex-end'
  },
  deleteButton: {
    color: theme.palette.white,
    backgroundColor: colors.red[600],
    '&:hover': {
      backgroundColor: colors.red[900]
    }
  },
  submitButton: {
    color: theme.palette.white,
    backgroundColor: colors.green[600],
    '&:hover': {
      backgroundColor: colors.green[900]
    }
  }
}));

export interface RoomSplitData {
  existingRoom: number[]
  newRoom: number[]
}

const SplitRoomPartyModal = (props: {
  className?: string,
  onClose: () => void,
  onUpdate?: () => void,
  open: boolean,
  roomParty: RoomParty,
}) => {
  const {open, onClose, onUpdate, roomParty, ...rest} = props;
  const classes = useStyles();
  const [formState, setFormState] = useState<RoomSplitData>({
    existingRoom: roomParty.members!.map(member => member.id),
    newRoom: [],
  });
  const [errors, setErrors] = useState<{isValid: boolean, error?: string}>({
    isValid: false,
    error: undefined,
  });
  const [sendSplitRoom, splitRoomData] = splitRoom();
  const {enqueueSnackbar} = useSnackbar();

  useEffect(() => {
    let isValid = true;
    let error;

    if(formState.newRoom.length === 0 || formState.existingRoom.length === 0) {
      isValid = false;
      error = 'You must split a room by putting members in both rooms.';
    }

    const start = moment(new Date(roomParty.start_date!));
    const end = moment(new Date(roomParty.end_date!));
    const roomAvailable = checkRoomAvailable(
        roomParty.hotel!,
        start,
        end,
        formState.newRoom.length
    );

    if(!roomAvailable) {
      isValid = false;
      error = 'There are no more rooms available in this hotel. Please contact an admin.';
    }

    setErrors({isValid, error});
  }, [formState])

  const updateUserRoom = (memberId: number, existingRoom: boolean) => {
    setFormState({
      ...formState,
      existingRoom: existingRoom ? [...formState.existingRoom.filter(id => id !== memberId), memberId] : [...formState.existingRoom.filter(id => id !== memberId)],
      newRoom: existingRoom ? [...formState.newRoom.filter(id => id !== memberId)] : [...formState.newRoom.filter(id => id !== memberId), memberId],
    });
  };


  const handleSubmit = () => {
    if (formState.newRoom.length > 0) {
      const sanitisedData: SplitRoomInput = {
        data: {
          id: roomParty.id,
          new_room: [...formState.newRoom],
        }
      };

      sendSplitRoom({variables: sanitisedData})
          .then(() => {
            enqueueSnackbar('Room Party Saved.', {variant: 'success'});
            onClose();
            if (onUpdate) onUpdate();
          })
          .catch(() => {
            enqueueSnackbar('Save Room Party Failed!', {variant: 'error'});
            onClose();
          });
    } else {
      onClose();
    }
  };

  if (!open) {
    return null;
  }

  return (
      <Modal onClose={onClose} open={open}>
        <Card {...rest} className={clsx(classes.root)}>
          <form>
            <LoadingOverlay loading={splitRoomData.loading}>
              <CardContent>
                <Typography gutterBottom variant="h3">Split Room Party</Typography>
                {errors.error && (
                    <Typography gutterBottom variant="body1" color={'error'}>{errors.error}</Typography>
                )}

                <Grid container spacing={3}>
                  <Grid item xs={6}><Typography variant={'body1'}>Party Member</Typography></Grid>
                  <Grid item xs={3}><Typography variant={'body1'}>Existing Room</Typography></Grid>
                  <Grid item xs={3}><Typography variant={'body1'}>New Room</Typography></Grid>
                  {roomParty.members && roomParty.members.map((member) => (
                      <React.Fragment key={member.id}>
                        <Grid item xs={6}>
                          <Typography variant={'body1'}>
                            {member.groupMember!.first_name + ' ' + member.groupMember!.last_name}
                          </Typography>
                        </Grid>
                        <Grid item xs={3}>
                          <Switch
                              checked={formState.existingRoom.indexOf(member.id) !== -1}
                              color="secondary"
                              edge="start"
                              onChange={(event, checked) => updateUserRoom(member.id, checked)}
                          />
                        </Grid>
                        <Grid item xs={3}>
                          <Switch
                              checked={formState.newRoom.indexOf(member.id) !== -1}
                              color="secondary"
                              edge="start"
                              onChange={(event, checked) => updateUserRoom(member.id, !checked)}
                          />
                        </Grid>
                      </React.Fragment>
                  ))}

                </Grid>

              </CardContent>
              <CardActions className={classes.actions}>
                <Button onClick={() => {
                  onClose();
                }} variant="contained">
                  <T phrase="Cancel"/>
                </Button>
                <Button className={classes.submitButton} onClick={handleSubmit} variant="contained" disabled={!errors.isValid}>
                  <T phrase="Confirm"/>
                </Button>
              </CardActions>
            </LoadingOverlay>
          </form>
        </Card>
      </Modal>
  );
};

export default SplitRoomPartyModal;
